/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  DatePicker,
  Card,
  message,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import moment from 'moment';

// Utils
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import {
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';

// Components
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';

// APIs
import {
  getSolicitudesPlazas,
  getSolicitudPlaza,
  postSolicitudPlaza,
  patchSolicitudPlaza,
  deleteSolicitudPlaza,
  autorizarSolicitudPlaza,
} from 'api/nominas/solicitudes/solicitudesPlazas';

import { getTiposDeNominasEmpleados } from 'api/nominas/catalogos/tiposNominasEmpleados';
import SolicitudesPlazasDetalles from './SolicitudesPlazasDetalles';

const SolicitudesPlazas = ({
  selected, // ID de la Solicitud Padre (configuración)
  permission,
  estatusInactivo,
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modales y Alertas
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [visibleConfirmInactivo, setVisibleConfirmInactivo] = useState(false);

  // Datos
  const [data, setData] = useState([]);
  const [tipoDeNominaEmpleado, setTipoDeNominaEmpleado] = useState([]);
  const [selectedData, setSelectedData] = useState({});
  const [disableFields, setDisableFields] = useState(false);

  // Estado visual para el campo Estatus en el formulario
  const [estatusVisual, setEstatusVisual] = useState('Activo');
  // ID del registro padre que se está editando en el modal (para pasar al hijo)
  const [currentEditingId, setCurrentEditingId] = useState(null);

  const formInstances = [form];

  // --- CARGA DE DATOS ---
  const fetchData = async () => {
    setLoading(true);
    try {
      if (selected) {
        // getSolicitudesPlazas filtrado por params
        const _data = await getSolicitudesPlazas({ solicitud: selected });
        setData(formatReceived(_data));
      }
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      if (selected) {
        setLoading(true);
        try {
          // Cargamos el catálogo de Tipos de Nómina Empleado (Solo autorizados)
          const [_tiposNominas, _data] = await Promise.all([
            getTiposDeNominasEmpleados({ estados_globales: ESTATUS_AUTORIZADO }),
            getSolicitudesPlazas({ solicitud: selected }),
          ]);

          if (mounted) {
            setTipoDeNominaEmpleado(_tiposNominas);
            setData(formatReceived(_data));
          }
        } catch (error) {
          onError(error);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [selected]);

  // --- ACCIONES DEL MODAL ---
  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
    setEstatusVisual('Activo');
    form.resetFields();
  };

  const onClickAdd = () => {
    form.resetFields();
    setEstatusVisual('Activo');
    onCancel();
    setVisible(true);
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    if (key) {
      const match = responseData || data.find((item) => item.id === key);
      // getSolicitudPlaza individual
      const _solicitudPlaza = await getSolicitudPlaza(match.id);

      setTimeout(() => {
        if (_solicitudPlaza) {
          form.resetFields();
          setTimeout(() => {
            // Formatear valores recibidos (fechas, etc)
            const values = formatReceived(_solicitudPlaza);

            // Ajuste visual del estatus según lógica de negocio
            if (values.vigencia_final) {
              setEstatusVisual('Inactivo');
            } else {
              setEstatusVisual('Activo');
            }

            form.setFieldsValue(values);
            setDisableFields(_solicitudPlaza?.estados_globales !== ESTATUS_ACTIVO);
            setVisible(true);
          });
        }
      });
    }
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;

      // Payload para el backend
      const payload = {
        ...values,
        solicitud: selected, // ID del padre
        // Formato de fecha para el backend (YYYY-MM-DD)
        vigencia_final: values.vigencia_final ? values.vigencia_final.format('YYYY-MM-DD') : null,
        // El estatus siempre se manda activo al crear/editar, a menos que sea autorización
        estados_globales: ESTATUS_ACTIVO,
      };

      // Patch o Post según si existe key
      const saved = await (key ? patchSolicitudPlaza(key, payload) : postSolicitudPlaza(payload));

      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        // deleteSolicitudPlaza
        const deleted = await deleteSolicitudPlaza(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        return;
      }
      // autorizarSolicitudPlaza
      const saved = await autorizarSolicitudPlaza(key);
      if (saved) {
        await fetchData();
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  // --- MANEJADORES DE INTERFAZ ---
  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickDelete = () => {
    if (selectedData?.estados_globales === ESTATUS_AUTORIZADO) {
      message.warn('No se puede eliminar un registro autorizado');
      return;
    }
    setVisibleAlert(true);
  };

  // Lógica visual: Si selecciona fecha, cambia a Inactivo visualmente
  const onDateChange = (date) => {
    if (date) {
      setEstatusVisual('Inactivo');
    } else {
      setEstatusVisual('Activo');
    }
  };

  // --- COLUMNAS DEL GRID ---
  const columns = [
    {
      titleText: 'Tipo de Nómina Empleado',
      dataIndex: 'plaza_nombre', // Asumiendo que el serializer devuelve este campo
      key: 'plaza_nombre',
      width: 300,
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigencia_final',
      key: 'vigencia_final',
      width: 150,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : '-'),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estatus_descripcion', // Asumiendo que el serializer devuelve descripción
      key: 'estatus_descripcion',
      width: 150,
    },
  ];

  return (
    <Row className="container" style={{ marginTop: '20px' }}>
      <p style={{
        color: '#D88F4F', fontWeight: 600, fontSize: '16px', width: '100%',
      }}
      >
        Agregar Plazas
      </p>
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          customActions={[
            {
              onClick: () => setVisibleAutorizar(true),
              permission: { model: 'solicitudplaza', action: 'autorizar' },
              icon: <CheckCircleOutlined />,
              text: 'Autorizar',
              disabledBySelection: true,
              disabled: selectedData?.estados_globales !== ESTATUS_ACTIVO,
              className: 'success',
            },
          ]}
          // Deshabilitar acciones generales si la solicitud padre está inactiva/autorizada
          disableAdd={estatusInactivo}
          disableDelete={selectedData?.estados_globales === ESTATUS_AUTORIZADO}
          permission={permission}
          allowExportPDF={false}
          titleTable=""
        />

        {/* --- MODAL (FORMULARIO) --- */}
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          width="80%"
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Plaza`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableFields}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
            initialValues={{
              estados_globales: ESTATUS_ACTIVO,
            }}
          >
            <Row gutter={24}>
              {/* COLUMNA 1: TIPO DE NOMINA EMPLEADO */}
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="tipo_nomina_empleado"
                  label="Tipo de Nómina Empleado"
                  hasFeedback
                  rules={[{ required: true, message: 'Requerido' }]}
                  extra={<span style={{ fontSize: '10px', color: '#d88f4f' }}>Campo vinculado con la tabla Tipo de Nómina Empleado</span>}
                >
                  <Select
                    keyProp="id"
                    labelProp="descripcion"
                    dataSource={tipoDeNominaEmpleado}
                    disabled={disableFields}
                    allowClear
                    placeholder="Seleccione..."
                  />
                </Form.Item>
              </Col>

              {/* COLUMNA 2: VIGENCIA FINAL */}
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="vigencia_final"
                  label="Vigencia Final"
                  extra={<span style={{ fontSize: '10px', color: '#d88f4f' }}>Campo de Fecha de captura manual</span>}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    onChange={onDateChange}
                    disabled={disableFields}
                  />
                </Form.Item>
              </Col>

              {/* COLUMNA 3: ESTATUS (VISUAL) */}
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  label="Estatus"
                  extra={<span style={{ fontSize: '10px', color: '#d88f4f' }}>El registro nace Activo, al Autorizar cambia a Autorizado.</span>}
                >
                  <Input
                    value={estatusVisual}
                    disabled
                    style={{ color: '#000', backgroundColor: '#f5f5f5' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* {currentEditingId && ( */}
            <SolicitudesPlazasDetalles
              solicitudPlazaId={currentEditingId}
              permission={permission}
              estatusInactivoPadre={disableFields}
            />
            {/* )} */}

            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>

        {/* --- MODALES DE CONFIRMACIÓN --- */}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content="¿Eliminar el registro seleccionado?"
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle="Autorizar Plaza"
          open={visibleAutorizar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => setVisibleConfirmInactivo(false)}
          ConfirmInactive
          onCancel={() => {
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
            setVisibleConfirmInactivo(false);
          }}
          open={visibleConfirmInactivo}
          contentTitle="Inactivar Plaza"
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default SolicitudesPlazas;
